// Per-pane content loading discipline for the compare view (RES-561, phase 8) — no
// React imports so the two guards stay unit-testable without mounting the container.
//
// Guards two container bugs found by the phase-5 review:
//   1. Stale responses: each side keeps a request sequence; a response only lands if
//      no newer load for that side has started, so switching the dropdown mid-flight
//      can't let the older response render under the new label.
//   2. Purged-ref refresh loop: a purged ver: ref triggers a dropdown refresh, which
//      re-runs the load effects (they depend on the options identity). The purged
//      result is cached per ref — the re-run short-circuits to the cached message
//      without refetching, and the refresh fires at most once per ref.
export default function createSideLoader() {
	const seqs = {};
	const purged = new Map();

	// fetcher: () => Promise<result> (fetchContentForRef — always resolves with a
	// {status, ...} object). setContent receives null while loading, then the result.
	// onPurged is called once per refKey when a fetch first reports status 'purged'.
	function load(side, refKey, fetcher, setContent, onPurged) {
		const seq = (seqs[side] = (seqs[side] || 0) + 1);
		const known = purged.get(refKey);
		if (known) {
			setContent(known);
			return Promise.resolve(known);
		}
		setContent(null);
		return fetcher().then((result) => {
			if (seq !== seqs[side])
				return result;
			setContent(result);
			if (result && result.status === 'purged' && !purged.has(refKey)) {
				purged.set(refKey, result);
				if (onPurged)
					onPurged();
			}
			return result;
		});
	}

	// Document-scope reset: bumps every side's sequence so responses still in flight
	// for the previous document fail their own staleness check (replacing the loader
	// instance would NOT do that — the orphaned closure would still land its result),
	// and clears the purged cache (version ids are per-ticker sequences, so a purge
	// must not leak across documents).
	load.reset = () => {
		Object.keys(seqs).forEach((side) => {
			seqs[side] += 1;
		});
		purged.clear();
	};

	return load;
}
